// Les règles de JEU du lecteur d'épisodes (C2, 25/09/2026), sans Vue ni Firebase.
//
// Un animal a 4 chapitres, un par stade. Le chapitre N se débloque à N âmes de
// cet animal (1 âme par scan) : 1 âme = chapitre 1 (bébé). Au-delà de 4 âmes,
// tout est ouvert. Ces fonctions décident ce que l'enfant peut ouvrir ; les
// règles Firestore, elles, laissent lire tous les épisodes publiés (le verrou
// est une règle de jeu, pas un secret).

export const STADES = ["bebe", "ado", "adulte", "mythique"];
export const LIBELLES = { bebe: "Bébé", ado: "Ado", adulte: "Adulte", mythique: "Mythique" };

/** Le stade du chapitre N (1 à 4), ou null. */
export function stadeDuChapitre(n) {
  return STADES[Number(n) - 1] || null;
}

/** L'identifiant du document publié : `24_ado`, écrit par publier_episode.py. */
export function idPublie(animalId, stade) {
  return `${animalId}_${stade}`;
}

function nombreAmes(ames) {
  const a = Math.floor(Number(ames) || 0);
  return a > 0 ? a : 0;
}

/** Vrai si le chapitre N est ouvert avec ce nombre d'âmes. */
export function estDebloque(n, ames) {
  const c = Number(n);
  return Number.isInteger(c) && c >= 1 && c <= STADES.length && nombreAmes(ames) >= c;
}

/** Âmes qu'il manque pour ouvrir le chapitre N (0 s'il est ouvert). */
export function amesManquantes(n, ames) {
  return Math.max(0, Number(n) - nombreAmes(ames));
}

/** Le chapitre qu'ouvre le bouton « Écouter son histoire » : le plus récent
 *  débloqué, celui que l'enfant vient de gagner. null sans aucune âme. */
export function chapitreParDefaut(ames) {
  const a = nombreAmes(ames);
  return a >= 1 ? Math.min(a, STADES.length) : null;
}

/** Le chapitre qu'ouvre le bouton, sachant lesquels sont PUBLIÉS : le plus
 *  récent à la fois gagné et publié. Un enfant à 3 âmes dont seul le chapitre 2
 *  existe écoute le 2, au lieu de tomber sur « bientôt disponible ». Sans
 *  aucun chapitre gagné ET publié, on retombe sur le dernier gagné (le lecteur
 *  dira alors « Histoire bientôt disponible »). */
export function chapitreAOuvrir(ames, publies) {
  const defaut = chapitreParDefaut(ames);
  if (!defaut) return null;
  const ok = (publies || []).map(Number).filter((n) => estDebloque(n, ames));
  return ok.length ? Math.max(...ok) : defaut;
}

/** Le chapitre réellement ÉCOUTABLE (gagné ET publié), ou null s'il n'y en a
 *  aucun. C'est lui qui décide d'afficher le bouton d'histoire (C3) : sans
 *  chapitre écoutable, on n'envoie pas l'enfant vers une impasse, on affiche
 *  « Histoire bientôt disponible ». */
export function chapitreDisponible(ames, publies) {
  const ok = (publies || []).map(Number).filter((n) => estDebloque(n, ames));
  return ok.length ? Math.max(...ok) : null;
}

/** Les 4 chapitres et leur état, pour le sélecteur du lecteur. */
export function chapitres(ames) {
  return STADES.map((stade, i) => ({
    n: i + 1,
    stade,
    libelle: LIBELLES[stade],
    debloque: estDebloque(i + 1, ames),
  }));
}

// ── Karaoké ─────────────────────────────────────────────────────────────────
// Le texte est découpé en MOTS = suites de caractères non blancs, exactement
// comme publier_episode.py compte les timings (un timing par mot). Les blancs
// entre les mots sont gardés tels quels pour l'affichage.

/** [{avant, mot}] : `avant` = les blancs qui précèdent le mot. */
export function decouper(texte) {
  const out = [];
  const re = /\S+/g;
  let m;
  let dernier = 0;
  const t = texte || "";
  while ((m = re.exec(t)) !== null) {
    out.push({ avant: t.slice(dernier, m.index), mot: m[0] });
    dernier = m.index + m[0].length;
  }
  return out;
}

/** Combien de mots ont commencé à l'instant t (secondes) : les mots 0..n-1
 *  sont « lus ». `timings` = départ de chaque mot, croissant. */
export function motsLus(timings, t) {
  const arr = timings || [];
  let bas = 0;
  let haut = arr.length;
  while (bas < haut) {
    const mil = (bas + haut) >> 1;
    if (arr[mil] <= t) bas = mil + 1;
    else haut = mil;
  }
  return bas;
}
